import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, Printer } from "lucide-react";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";

/**
 * Read-only dialog that shows full detail of a historic cash movement.
 * Props:
 *   open: boolean
 *   onOpenChange(open)
 *   movement: the selected historic movement object (or null)
 *   onReprint(movement): callback invoked when the user clicks "Reimprimir Ticket"
 */
export default function MovementDetailDialog({ open, onOpenChange, movement, onReprint }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Detalle del Movimiento
          </DialogTitle>
          <DialogDescription>
            {movement?.operation_number || `#${movement?.id?.substring(0, 8)}`}
          </DialogDescription>
        </DialogHeader>

        {movement && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500">Fecha y Hora</p>
                <p className="font-semibold">
                  {new Date(movement.created_at).toLocaleDateString("es-ES", {
                    weekday: "long", year: "numeric", month: "long", day: "numeric"
                  })}
                </p>
                <p className="text-sm text-slate-600">
                  {new Date(movement.created_at).toLocaleTimeString("es-ES")}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${movement.type === "expense" ? "bg-red-50" : "bg-emerald-500/10"}`}>
                <p className="text-xs text-slate-500">Importe</p>
                <p className={`text-2xl font-bold ${movement.type === "expense" ? "text-red-600" : "text-emerald-600"}`}>
                  {movement.type === "expense" ? "-" : "+"}{formatCurrency((movement.amount || 0))}
                </p>
                <Badge className={movement.payment_method === "cash" ? "bg-emerald-600 text-white border-0" : "bg-purple-600 text-white border-0"}>
                  {movement.payment_method === "cash" ? "Efectivo" : "Tarjeta"}
                </Badge>
              </div>
            </div>

            {movement.customer_name && (
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-100">
                <p className="text-xs text-blue-600 font-medium">Cliente</p>
                <p className="font-bold text-blue-900">{movement.customer_name}</p>
                {movement.customer_dni && (
                  <p className="text-sm text-blue-700">DNI: {movement.customer_dni}</p>
                )}
              </div>
            )}

            <div className="p-3 rounded-lg bg-slate-100">
              <p className="text-xs text-slate-500">Concepto</p>
              <p className="font-medium">{movement.concept || movement.description || "-"}</p>
            </div>

            {movement.items && movement.items.length > 0 && (
              <div className="p-3 rounded-lg border">
                <p className="text-xs text-slate-500 mb-2">Artículos</p>
                <div className="space-y-1">
                  {movement.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.item_type || item.name || "Artículo"} {item.size && `(${item.size})`}</span>
                      <span className="font-medium">{formatCurrency((item.subtotal || item.unit_price || 0))}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {movement.notes && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-100">
                <p className="text-xs text-amber-600">Notas</p>
                <p className="text-sm">{movement.notes}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>
            Cerrar
          </Button>
          <Button
            onClick={() => {
              onReprint?.(movement);
              onOpenChange?.(false);
            }}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Printer className="h-4 w-4 mr-2" />
            Reimprimir Ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
